import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";

export interface AdminAppFlag {
  key: string;
  enabled: boolean;
  defaultEnabled: boolean;
  description: string;
}

const QUERY_KEY = ["/api/admin/app-flags"] as const;

/**
 * Super-admin switches for global app flags (shared/app-flags.ts): flip them
 * without a deploy. Changes take effect within ~30 seconds (server cache) and
 * are written to the admin audit log.
 */
export function AppFlagsPanel() {
  const { toast } = useToast();
  const flagsQuery = useQuery<AdminAppFlag[]>({ queryKey: QUERY_KEY });
  const setFlag = useMutation({
    mutationFn: async (input: { key: string; enabled: boolean }) => {
      const res = await apiRequest("POST", "/api/admin/app-flags", input);
      return (await res.json()) as { key: string; enabled: boolean };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: `${r.key} ${r.enabled ? "on" : "off"}`, description: "Takes effect within about 30 seconds." });
    },
    onError: (err) => {
      toast({ title: "Couldn't change the flag", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4" data-testid="panel-app-flags">
      <div>
        <h2 className="text-lg font-semibold">App flags</h2>
        <p className="text-sm text-muted-foreground">
          Global on/off switches. Changes apply without a deploy and are recorded in the audit log.
        </p>
      </div>
      {flagsQuery.isLoading && <Skeleton className="h-24 w-full" />}
      {flagsQuery.isError && (
        <p className="text-sm text-red-600" data-testid="text-app-flags-error">
          {parseErrorMessage(flagsQuery.error, "Couldn't load app flags.")}
        </p>
      )}
      <div className="divide-y rounded-md border">
        {(Array.isArray(flagsQuery.data) ? flagsQuery.data : []).map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-4 p-3" data-testid={`row-app-flag-${f.key}`}>
            <div className="min-w-0">
              <Label htmlFor={`switch-app-flag-${f.key}`} className="font-mono text-sm">{f.key}</Label>
              <p className="text-sm text-muted-foreground">{f.description}</p>
              {f.enabled !== f.defaultEnabled && (
                <Badge variant="outline" className="mt-1 text-xs">Changed from default ({f.defaultEnabled ? "on" : "off"})</Badge>
              )}
            </div>
            <Switch
              id={`switch-app-flag-${f.key}`}
              checked={f.enabled}
              disabled={setFlag.isPending}
              onCheckedChange={(enabled) => setFlag.mutate({ key: f.key, enabled })}
              data-testid={`switch-app-flag-${f.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
